import { useState, useEffect, useRef, useCallback } from 'react';
import logger from '@/utils/logger';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Search, Calendar, Eye, Tag, Loader2, Package2, FileText, ArrowRight, Mail, Send, Clock, Pin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useFormAnnouncer, FormError } from '@/components/FormErrorAnnouncer';
import PublicNavbar from '@/components/PublicNavbar';
import { LanguageFlag } from '@/components/LanguageFlag';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Blog() {
  const { t, i18n } = useTranslation();
  const { categorySlug } = useParams();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(categorySlug || searchParams.get('category') || 'all');
  const [selectedTag, setSelectedTag] = useState(searchParams.get('tag') || '');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterError, setNewsletterError] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const observer = useRef();
  const navigate = useNavigate();
  const { announceError, announceSuccess } = useFormAnnouncer();

  // Category labels with translations
  const getCategoryLabel = (categoryKey) => {
    return t(`blog.category.${categoryKey}`, categoryKey);
  };

  // Sync category from URL param
  useEffect(() => {
    if (categorySlug) {
      setCategory(categorySlug);
    }
  }, [categorySlug]);

  const lastPostRef = useCallback(node => {
    if (loadingMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setCurrentPage(prev => prev + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loadingMore, hasMore]);

  useEffect(() => {
    loadCategories();
    loadTags();
  }, []);

  useEffect(() => {
    setPosts([]);
    setCurrentPage(1);
    setHasMore(true);
  }, [searchTerm, category, selectedTag, i18n.language]);

  // Normalize language code for API (handle variants like 'en-US@posix')
  const getApiLanguage = useCallback(() => {
    const lang = i18n.resolvedLanguage || i18n.language || 'en';
    return lang.split('-')[0].split('@')[0];  // 'en-US@posix' -> 'en'
  }, [i18n.resolvedLanguage, i18n.language]);

  const loadPosts = useCallback(async () => {
    if (currentPage === 1) setLoading(true);
    else setLoadingMore(true);

    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('page_size', '10');
      // Note: We no longer filter by language so all posts are shown
      // Each post has a language indicator in the UI
      if (searchTerm) params.append('search', searchTerm);
      if (category && category !== 'all') params.append('category', category);
      if (selectedTag) params.append('tag', selectedTag);

      const response = await axios.get(`${API}/blog?${params.toString()}`);
      const newItems = response.data.items || [];

      if (currentPage === 1) {
        setPosts(newItems);
      } else {
        setPosts(prev => [...prev, ...newItems]);
      }

      setTotalItems(response.data.total || 0);
      setHasMore(currentPage < (response.data.total_pages || 1));
    } catch (error) {
      logger.error('Failed to load posts:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [currentPage, searchTerm, category, selectedTag]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/blog/categories`);
      setCategories(response.data || []);
    } catch (error) {
      logger.error('Failed to load categories:', error);
    }
  };

  const loadTags = async () => {
    try {
      const response = await axios.get(`${API}/blog/tags`);
      setTags(response.data || []);
    } catch (error) {
      logger.error('Failed to load tags:', error);
    }
  };

  const handleSearch = () => {
    if (category && category !== 'all') {
      const params = new URLSearchParams();
      if (searchTerm) params.set('search', searchTerm);
      if (selectedTag) params.set('tag', selectedTag);
      const queryString = params.toString();
      navigate(`/blog/category/${category}${queryString ? `?${queryString}` : ''}`);
    } else {
      const params = new URLSearchParams();
      if (searchTerm) params.set('search', searchTerm);
      if (selectedTag) params.set('tag', selectedTag);
      setSearchParams(params);
      if (categorySlug) {
        navigate(`/blog${params.toString() ? `?${params.toString()}` : ''}`);
      }
    }
  };

  const handleCategoryChange = (newCategory) => {
    setCategory(newCategory);
    setPosts([]);
    setCurrentPage(1);
    setHasMore(true);
    
    if (newCategory && newCategory !== 'all') {
      navigate(`/blog/category/${newCategory}`);
    } else if (categorySlug) {
      navigate('/blog');
    }
  };

  const formatDate = (dateStr) => {
    const locale = i18n.language === 'fi' ? 'fi-FI' : 'en-US';
    return new Date(dateStr).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const highlightText = (text, query) => {
    if (!query || !text) return text;
    
    const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    
    return parts.map((part, index) => 
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={`highlight-${index}-${part}`} className="bg-yellow-200 text-yellow-900 px-0.5 rounded">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const handleNewsletterSubscribe = async (e) => {
    e.preventDefault();
    setNewsletterError('');
    
    if (!newsletterEmail.trim()) {
      const errorMsg = t('newsletter.emailRequired');
      setNewsletterError(errorMsg);
      announceError(errorMsg);
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newsletterEmail)) {
      const errorMsg = t('newsletter.emailInvalid');
      setNewsletterError(errorMsg);
      announceError(errorMsg);
      return;
    }

    setSubscribing(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, {
        email: newsletterEmail
      });
      // Show appropriate message based on response
      if (response.data.pending_confirmation || !response.data.already_subscribed) {
        toast.success(t('newsletter.confirmationSent'));
        toast.info(t('newsletter.checkInbox'), { duration: 6000 });
      } else {
        toast.success(response.data.message);
      }
      announceSuccess(response.data.message);
      setNewsletterEmail('');
      setNewsletterError('');
    } catch (error) {
      logger.error('Newsletter subscription failed:', error);
      const errorMsg = error.response?.data?.detail || t('common.error');
      toast.error(errorMsg);
      announceError(errorMsg);
    } finally {
      setSubscribing(false);
    }
  };

  const calculateReadingTime = (content) => {
    if (!content) return 1;
    const wordsPerMinute = 200;
    const wordCount = content.trim().split(/\s+/).length;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return minutes < 1 ? 1 : minutes;
  };

  if (loading && currentPage === 1) {
    return (
      <div className="min-h-screen bg-background">
        <PublicNavbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
            <p className="mt-4 text-muted-foreground">{t('blog.loadingPosts')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PublicNavbar />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          {categorySlug ? (
            <>
              <div className="flex items-center justify-center gap-2 mb-4">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate('/blog')}
                  className="rounded-full text-muted-foreground hover:text-foreground"
                >
                  ← {t('blog.allPosts')}
                </Button>
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-extrabold tracking-tight mb-4">
                <span className="text-primary">{getCategoryLabel(categorySlug)}</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {t('blog.browseCategory')} {getCategoryLabel(categorySlug)}
              </p>
            </>
          ) : (
            <>
              <h1 className="text-4xl md:text-5xl font-heading font-extrabold tracking-tight mb-4">
                {t('blog.title').split('&')[0]} <span className="text-primary">&{t('blog.title').split('&')[1] || ''}</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {t('blog.subtitle')}
              </p>
            </>
          )}
        </div>

        {/* Category Quick Links */}
        {!categorySlug && categories.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {categories.map(cat => (
              <Button
                key={cat.category}
                variant="outline"
                size="sm"
                onClick={() => navigate(`/blog/category/${cat.category}`)}
                className="rounded-full capitalize"
              >
                {getCategoryLabel(cat.category)}
                <span className="ml-1 text-muted-foreground">({cat.count})</span>
              </Button>
            ))}
          </div>
        )}

        {/* Search & Filters */}
        <div className="rounded-3xl bg-white border border-stone-100 p-6 mb-8 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={t('blog.search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-12 rounded-xl"
                />
              </div>
            </div>
            <Select value={category} onValueChange={handleCategoryChange}>
              <SelectTrigger className="rounded-xl">
                <SelectValue placeholder={t('blog.allCategories')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('blog.allCategories')}</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.category} value={cat.category}>
                    {getCategoryLabel(cat.category)} ({cat.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleSearch} className="rounded-full bg-primary text-primary-foreground">
              <Search className="h-4 w-4 mr-2" />
              {t('blog.searchButton')}
            </Button>
          </div>
          
          {/* Tags */}
          {tags.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-sm text-muted-foreground mr-2">{t('blog.tags')}:</span>
              {tags.slice(0, 10).map(tg => (
                <button
                  key={tg.tag}
                  onClick={() => {
                    setSelectedTag(selectedTag === tg.tag ? '' : tg.tag);
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    selectedTag === tg.tag
                      ? 'bg-primary text-white'
                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  #{tg.tag}
                </button>
              ))}
            </div>
          )}
          
          <div className="mt-4 text-sm text-muted-foreground">
            {totalItems} {totalItems !== 1 ? t('blog.postsFound') : t('blog.postFound')}
          </div>
        </div>

        {/* Posts List */}
        {posts.length === 0 ? (
          <div className="text-center py-20 rounded-3xl bg-white border border-stone-100">
            <FileText className="h-24 w-24 text-muted-foreground/30 mx-auto mb-6" />
            <h3 className="text-2xl font-heading font-semibold mb-2">{t('blog.noPosts')}</h3>
            <p className="text-muted-foreground mb-6">
              {t('blog.noPostsMessage')}
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {posts.map((post, index) => (
              <article
                key={post.post_id}
                ref={index === posts.length - 1 ? lastPostRef : null}
                onClick={() => navigate(`/blog/${post.slug || post.post_id}`)}
                className="rounded-3xl bg-white border border-stone-100 overflow-hidden shadow-sm hover:shadow-xl transition-all cursor-pointer group"
              >
                <div className="md:flex">
                  {post.featured_image && (
                    <div className="md:w-1/3 aspect-video md:aspect-auto">
                      <img
                        src={post.featured_image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <div className={`p-8 ${post.featured_image ? 'md:w-2/3' : 'w-full'}`}>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mb-3">
                      {post.language && (
                        <span className="flex items-center px-2 py-1 rounded-full bg-stone-100 text-xs">
                          <LanguageFlag language={post.language} size="xs" />
                        </span>
                      )}
                      {post.is_pinned && (
                        <span className="flex items-center px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                          <Pin className="h-3 w-3 mr-1" />
                          {t('blog.pinned')}
                        </span>
                      )}
                      <span className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formatDate(post.created_at)}
                      </span>
                      <span className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {calculateReadingTime(post.content)} {t('blog.minRead')}
                      </span>
                      <span className="flex items-center">
                        <Eye className="h-4 w-4 mr-1" />
                        {post.view_count}
                      </span>
                      <span className="px-2 py-1 rounded-full bg-stone-100 text-xs capitalize">
                        {getCategoryLabel(post.category)}
                      </span>
                    </div>
                    
                    <h2 className="text-2xl font-heading font-bold mb-3 group-hover:text-primary transition-colors">
                      {highlightText(post.title, searchTerm)}
                    </h2>
                    
                    <p className="text-muted-foreground line-clamp-3 mb-4">
                      {highlightText(post.excerpt, searchTerm)}
                    </p>
                    
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags.slice(0, 4).map(tag => (
                          <span key={tag} className="flex items-center text-xs text-stone-500">
                            <Tag className="h-3 w-3 mr-1" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    <span className="inline-flex items-center text-primary font-medium group-hover:underline">
                      {t('blog.readMore')} <ArrowRight className="h-4 w-4 ml-1" />
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {loadingMore && (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {!hasMore && posts.length > 0 && (
          <p className="text-center text-muted-foreground py-8">
            {t('blog.seenAll')} {totalItems} {t('blog.posts')}
          </p>
        )}

        {/* Newsletter Signup */}
        <section className="rounded-3xl bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 p-8 md:p-12 mt-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Mail className="h-7 w-7 text-primary" />
            </div>
            <h3 className="text-2xl md:text-3xl font-heading font-bold mb-3">
              {t('newsletter.title')}
            </h3>
            <p className="text-muted-foreground mb-6">
              {t('newsletter.subtitle')}
            </p>
            <form onSubmit={handleNewsletterSubscribe} className="flex flex-col gap-3 max-w-md mx-auto" noValidate>
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="email"
                  placeholder={t('newsletter.placeholder')}
                  value={newsletterEmail}
                  onChange={(e) => {
                    setNewsletterEmail(e.target.value);
                    if (newsletterError) setNewsletterError('');
                  }}
                  className={`flex-1 rounded-full px-6 ${newsletterError ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                  data-testid="newsletter-email-input"
                  aria-describedby={newsletterError ? "newsletter-error" : undefined}
                  aria-invalid={!!newsletterError}
                />
                <Button
                  type="submit"
                  disabled={subscribing}
                  className="rounded-full bg-primary text-primary-foreground px-8"
                  data-testid="newsletter-subscribe-btn"
                >
                  {subscribing ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-label={t('newsletter.subscribing')} />
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" aria-hidden="true" />
                      {t('newsletter.subscribe')}
                    </>
                  )}
                </Button>
              </div>
              {newsletterError && (
                <FormError id="newsletter-error" message={newsletterError} className="text-center" />
              )}
            </form>
            <p className="text-xs text-muted-foreground mt-4">
              {t('newsletter.noSpam')}
            </p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-stone-100 bg-white mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Package2 className="h-6 w-6 text-primary" />
            <span className="text-xl font-heading font-bold">Even Rounds</span>
          </div>
          <p className="text-sm text-stone-500">
            © 2026 Even Rounds. {t('common.madeWithLove')}
          </p>
        </div>
      </footer>
    </div>
  );
}
